import { IAccessLevel } from '@quesmed/types/models';
import { AiTwotoneDelete } from 'react-icons/ai';
import { BiCommentDots } from 'react-icons/bi';
import { BsFileImage, BsMarkdownFill } from 'react-icons/bs';
import { FaAws, FaUserMd } from 'react-icons/fa';
import { FaArrowDownWideShort, FaRegFileExcel, FaToolbox, FaUserDoctor, FaVideo } from 'react-icons/fa6';
import { FiBookOpen, FiFolderPlus } from 'react-icons/fi';
import { GrBug, GrClone, GrCodeSandbox, GrStripe, GrValidate } from 'react-icons/gr';
// import { HiMiniQueueList } from 'react-icons/hi2';
import { ImBlogger2, ImLab } from 'react-icons/im';
import { IoMdEye, IoMdMail } from 'react-icons/io';
import { IoStatsChartOutline } from 'react-icons/io5';
import type { IconType } from 'react-icons/lib';
import {
  MdEditSquare,
  MdGroups,
  MdMenuBook,
  MdNotificationsActive,
  MdOutlineDiscount,
  MdOutlineSpaceDashboard,
  MdOutlineVpnKey,
  MdQuiz,
  MdSchool,
} from 'react-icons/md';
import { PiBroomDuotone, PiFileSql } from 'react-icons/pi';
import { TbBrandAlgolia, TbCalculator, TbMessageChatbot, TbRobot } from 'react-icons/tb';
import { VscEditSession } from 'react-icons/vsc';
import type { EEnvironment } from './globals';
import { ROUTES } from './routes';

export enum EMenu {
  MAIN = 'MAIN',
  CONTENT = 'CONTENT',
  ASSET = 'ASSET',
  ALGOLIA = 'ALGOLIA',
  SENDGRID = 'SENDGRID',
  DATABASE = 'DATABASE',
  USER = 'USER',
  // SUBSCRIPTION = 'SUBSCRIPTION',
  AI = 'AI',
  OSCE = 'OSCE',
  TUTOR = 'TUTOR',
  STUDY_GROUP = 'STUDY GROUPS',
  UNIVERSITY = 'UNIVERSITY',
  SETTINGS = 'SETTINGS',
}

export interface IMenuListItem {
  label: string;
  icon: IconType;
  href: string;
  enabled: boolean;
  tooltip?: string;
  accessLevels: IAccessLevel[];
}

export type IMenuList = IMenuListItem | IMenuListHeader;

export interface IMenuListHeader extends Omit<IMenuListItem, 'href'> {
  items: IMenuList[];
}

export const isHeader = (item: IMenuList): item is IMenuListHeader => {
  return 'items' in item;
};

const allowedAccessLevels = [
  IAccessLevel.EDITOR,
  IAccessLevel.SUPER_ADMIN,
  IAccessLevel.ADMINISTRATOR,
  IAccessLevel.SENIOR_EDITOR,
];
const allowedAccessLevelsForAdmin = [IAccessLevel.SUPER_ADMIN, IAccessLevel.ADMINISTRATOR];

const Menus: Record<EMenu, IMenuList[]> = {
  [EMenu.MAIN]: [
    {
      label: 'Dashboard',
      href: ROUTES.MAIN,
      icon: MdOutlineSpaceDashboard,
      enabled: true,
      accessLevels: [
        IAccessLevel.WRITER,
        IAccessLevel.EDITOR,
        IAccessLevel.SENIOR_EDITOR,
        IAccessLevel.MARKETING,
        IAccessLevel.SUPER_ADMIN,
        IAccessLevel.ADMINISTRATOR,
      ],
    },
  ],
  [EMenu.AI]: [
    {
      label: 'High Yield Explanation',
      href: ROUTES.AI.HIGH_YIELD,
      icon: FaArrowDownWideShort,
      accessLevels: allowedAccessLevels,
      enabled: true,
    },
    {
      label: 'Prompt Lab',
      href: ROUTES.AI.PROMPT_LAB,
      icon: ImLab,
      accessLevels: allowedAccessLevels,
      enabled: true,
    },
  ],
  [EMenu.OSCE]: [
    {
      label: 'Dashboard',
      href: ROUTES.OSCE.STATS,
      icon: IoStatsChartOutline,
      accessLevels: allowedAccessLevelsForAdmin,
      enabled: true,
    },
    {
      label: 'Sessions',
      href: ROUTES.OSCE.SESSIONS,
      icon: VscEditSession,
      accessLevels: allowedAccessLevelsForAdmin,
      enabled: true,
    },
    {
      label: 'Stations',
      href: ROUTES.OSCE.STATIONS,
      icon: MdMenuBook,
      accessLevels: allowedAccessLevelsForAdmin,
      enabled: true,
    },
    {
      label: 'Calculator',
      href: ROUTES.OSCE.CALCULATOR,
      icon: TbCalculator,
      accessLevels: allowedAccessLevelsForAdmin,
      enabled: true,
    },
  ],
  [EMenu.TUTOR]: [
    {
      label: 'Dashboard',
      href: ROUTES.TUTOR.STATS,
      icon: TbRobot,
      accessLevels: allowedAccessLevelsForAdmin,
      enabled: true,
    },
    {
      label: 'Sessions',
      href: ROUTES.TUTOR.SESSIONS,
      icon: VscEditSession,
      accessLevels: allowedAccessLevelsForAdmin,
      enabled: true,
    },
  ],
  [EMenu.STUDY_GROUP]: [
    {
      label: 'Groups',
      href: ROUTES.STUDY_GROUP.GROUPS,
      icon: MdGroups,
      accessLevels: allowedAccessLevelsForAdmin,
      enabled: true,
    },
    {
      label: 'Product settings',
      href: ROUTES.STUDY_GROUP.SETTINGS,
      icon: MdOutlineDiscount,
      accessLevels: allowedAccessLevelsForAdmin,
      enabled: true,
    },
  ],
  [EMenu.UNIVERSITY]: [
    {
      label: 'SSO',
      href: ROUTES.SSO.DASHBOARD,
      icon: MdOutlineVpnKey,
      accessLevels: allowedAccessLevelsForAdmin,
      enabled: true,
    },
    {
      label: 'Accounts',
      href: ROUTES.UNIVERSITY_ACCOUNTS.LIST,
      icon: MdSchool,
      accessLevels: [IAccessLevel.SUPER_ADMIN],
      enabled: true,
    },
  ],
  [EMenu.SETTINGS]: [
    {
      label: 'Prompts',
      href: ROUTES.SETTINGS.PROMPTS,
      icon: TbMessageChatbot,
      accessLevels: allowedAccessLevelsForAdmin,
      enabled: true,
    },
  ],
  [EMenu.ALGOLIA]: [
    {
      label: 'Refresh Index',
      href: ROUTES.ALGOLIA.MAIN,
      icon: TbBrandAlgolia,
      enabled: true,
      accessLevels: [...allowedAccessLevelsForAdmin],
    },
  ],
  [EMenu.SENDGRID]: [
    {
      label: 'Sync Contacts',
      href: ROUTES.SENDGRID.MAIN,
      icon: IoMdMail,
      enabled: true,
      accessLevels: [...allowedAccessLevelsForAdmin],
    },
  ],
  [EMenu.CONTENT]: [
    {
      label: 'Edit',
      icon: MdEditSquare,
      enabled: true,
      items: [
        {
          label: 'Question',
          href: ROUTES.CONTENT.EDIT.QUESTION,
          icon: MdQuiz,
          enabled: true,
          accessLevels: allowedAccessLevels,
        },
        {
          label: 'Chapter',
          href: ROUTES.CONTENT.EDIT.CHAPTER,
          icon: MdMenuBook,
          enabled: true,
          accessLevels: allowedAccessLevels,
        },
        {
          label: 'Station',
          href: ROUTES.CONTENT.EDIT.STATION,
          icon: FaUserDoctor,
          enabled: true,
          accessLevels: allowedAccessLevels,
        },
      ],
      accessLevels: allowedAccessLevels,
    },
    {
      label: 'Review',
      icon: GrValidate,
      items: [
        {
          label: 'All comments',
          href: ROUTES.CONTENT.REVIEW.COMMENTS,
          icon: BiCommentDots,
          enabled: true,
          accessLevels: [...allowedAccessLevelsForAdmin, IAccessLevel.SENIOR_EDITOR],
        },
        {
          label: 'Reviewed comments',
          href: ROUTES.CONTENT.REVIEW.QUESTIONS,
          icon: IoMdEye,
          enabled: true,
          accessLevels: [...allowedAccessLevelsForAdmin, IAccessLevel.SENIOR_EDITOR],
        },
        {
          label: 'Weekly SQL View',
          href: ROUTES.CONTENT.REVIEW.WEEKLY_SQL_VIEW,
          icon: FaRegFileExcel,
          enabled: true,
          accessLevels: [...allowedAccessLevelsForAdmin, IAccessLevel.SENIOR_EDITOR],
        },
        {
          label: 'SQL Search',
          href: ROUTES.CONTENT.REVIEW.WEEKLY_SEARCH,
          icon: PiFileSql,
          enabled: true,
          accessLevels: [...allowedAccessLevelsForAdmin, IAccessLevel.SENIOR_EDITOR],
        },
        {
          label: 'Help Scout',
          href: ROUTES.CONTENT.REVIEW.QUERIES,
          icon: GrBug,
          enabled: true,
          accessLevels: [...allowedAccessLevelsForAdmin, IAccessLevel.SENIOR_EDITOR],
        },
      ],
      enabled: false,
      accessLevels: allowedAccessLevels,
    },
    {
      label: 'Job',
      icon: FaToolbox,
      items: [
        {
          label: 'List',
          href: ROUTES.CONTENT.JOB.LIST,
          icon: FiBookOpen,
          enabled: true,
          accessLevels: [...allowedAccessLevels, IAccessLevel.WRITER],
        },
        {
          label: 'Create',
          href: ROUTES.CONTENT.JOB.CREATE,
          icon: FiFolderPlus,
          enabled: true,
          accessLevels: [...allowedAccessLevels, IAccessLevel.WRITER],
        },
        {
          label: 'Example',
          href: ROUTES.CONTENT.JOB.EXAMPLE,
          icon: GrCodeSandbox,
          enabled: true,
          accessLevels: [...allowedAccessLevels, IAccessLevel.WRITER],
        },
        // {
        //   label: 'Detail',
        //   href: ROUTES.CONTENT.JOB.DETAIL,
        //   icon: FaListUl,
        //   enabled: true,
        //   accessLevels: allowedAccessLevels,
        // },
      ],
      enabled: true,
      accessLevels: [...allowedAccessLevels, IAccessLevel.WRITER],
    },

    // {
    //   label: 'Validate',
    //   icon: GrValidate,
    //   items: [
    //     // {
    //     //   label: 'Ucat',
    //     //   href: ROUTES.CONTENT.VALIDATE.UCAT,
    //     //   icon: IoIosList,
    //     //   enabled: true,
    //     //   accessLevels: allowedAccessLevels,
    //     // },
    //     // {
    //     //   label: 'Question',
    //     //   href: ROUTES.CONTENT.VALIDATE.QUESTION,
    //     //   icon: IoIosList,
    //     //   enabled: true,
    //     //   accessLevels: allowedAccessLevels,
    //     // },
    //     // {
    //     //   label: 'station',
    //     //   href: ROUTES.CONTENT.VALIDATE.STATION,
    //     //   icon: IoIosList,
    //     //   enabled: true,
    //     //   accessLevels: allowedAccessLevels,
    //     // },
    //     // {
    //     //   label: 'Mock Test',
    //     //   href: ROUTES.CONTENT.VALIDATE.MOCK_TEST,
    //     //   icon: IoIosList,
    //     //   enabled: true,
    //     //   accessLevels: allowedAccessLevels,
    //     // },
    //     {
    //       label: 'Book',
    //       href: ROUTES.CONTENT.VALIDATE.BOOK,
    //       icon: IoIosList,
    //       enabled: true,
    //       accessLevels: allowedAccessLevels,
    //     },
    //   ],
    //   enabled: true,
    //   accessLevels: allowedAccessLevels,
    // },
    // {
    //   label: 'Batch',
    //   icon: HiMiniQueueList,
    //   items: [
    //     {
    //       label: 'Visibility',
    //       href: ROUTES.CONTENT.BATCH.PUBLIC,
    //       icon: AiTwotoneEyeInvisible,
    //       enabled: true,
    //       accessLevels: allowedAccessLevels,
    //     },
    //     // {
    //     //   label: 'Overwrite',
    //     //   href: ROUTES.CONTENT.BATCH.OVERWRITE,
    //     //   icon: BiSolidEdit,
    //     //   enabled: true,
    //     //   accessLevels: allowedAccessLevels,
    //     // },
    //     {
    //       label: 'Import',
    //       href: ROUTES.CONTENT.BATCH.IMPORT,
    //       icon: TbDatabaseImport,
    //       enabled: true,
    //       accessLevels: allowedAccessLevels,
    //     },
    //   ],
    //   enabled: true,
    //   accessLevels: allowedAccessLevels,
    // },
    {
      label: 'Blogs',
      icon: ImBlogger2,
      href: ROUTES.CONTENT.BLOG.MAIN,
      enabled: true,
      accessLevels: [...allowedAccessLevels, IAccessLevel.WRITER],
    },
    {
      label: 'Clone Content',
      href: ROUTES.CONTENT.CLONE,
      icon: GrClone,
      enabled: true,
      accessLevels: allowedAccessLevelsForAdmin,
    },
    {
      label: 'Templates',
      href: ROUTES.CONTENT.TEMPLATE,
      icon: BsMarkdownFill,
      enabled: true,
      accessLevels: allowedAccessLevels,
    },

    {
      label: 'Delete Records',
      icon: AiTwotoneDelete,
      href: ROUTES.CONTENT.DELETE.MAIN,
      enabled: true,
      accessLevels: [...allowedAccessLevelsForAdmin, IAccessLevel.SENIOR_EDITOR],
    },
    {
      label: 'Manage Cache',
      icon: PiBroomDuotone,
      href: ROUTES.DATABASE.CACHE,
      enabled: true,
      accessLevels: allowedAccessLevelsForAdmin,
    },
  ],
  [EMenu.ASSET]: [
    {
      label: 'DB Images',
      icon: BsFileImage,
      href: ROUTES.ASSET.IMAGE.MAIN,
      enabled: true,
      accessLevels: allowedAccessLevels,
    },
    {
      label: 'AWS bucket',
      icon: FaAws,
      href: ROUTES.ASSET.AWS.MAIN,
      enabled: true,
      accessLevels: allowedAccessLevels,
    },
    {
      label: 'Video',
      icon: FaVideo,
      href: ROUTES.ASSET.VIDEO.MAIN,
      enabled: true,
      accessLevels: allowedAccessLevels,
    },
  ],
  [EMenu.USER]: [
    {
      label: 'Sessions',
      icon: VscEditSession,
      href: ROUTES.USER.SESSIONS,
      enabled: true,
      accessLevels: allowedAccessLevels,
    },
    {
      label: 'Info',
      icon: FaUserMd,
      href: ROUTES.USER.INFO,
      enabled: true,
      accessLevels: allowedAccessLevels,
    },
    {
      label: 'Subs',
      icon: GrStripe,
      href: ROUTES.USER.SUBS,
      enabled: true,
      accessLevels: allowedAccessLevelsForAdmin,
    },
    {
      label: 'Notification',
      icon: MdNotificationsActive,
      href: ROUTES.DATABASE.NOTIFICATION,
      enabled: true,
      accessLevels: allowedAccessLevelsForAdmin,
    },
  ],
  [EMenu.DATABASE]: [
    // {
    //   label: 'Refresh MTV',
    //   icon: RiRefreshLine,
    //   href: ROUTES.DATABASE.REFRESH_MTV,
    //   enabled: true,
    //   accessLevels: allowedAccessLevelsForAdmin,
    // },
    // {
    //   label: 'Cron',
    //   icon: RxLapTimer,
    //   href: ROUTES.DATABASE.CRON,
    //   enabled: true,
    //   accessLevels: allowedAccessLevelsForAdmin,
    // },
  ],
  // [EMenu.SUBSCRIPTION]: [
  //   {
  //     label: 'Revenue',
  //     icon: PiCurrencyDollarBold,
  //     href: ROUTES.SUBSCRIPTION.TRANSACTIONS,
  //     enabled: true,
  //     accessLevels: [IAccessLevel.SUPER_ADMIN],
  //   },
  //   {
  //     label: 'Reviews',
  //     icon: GoCodeReview,
  //     href: ROUTES.SUBSCRIPTION.REVIEWS,
  //     enabled: true,
  //     accessLevels: allowedAccessLevelsForAdmin,
  //   },
  // ],
};

const editorMenu: Partial<Record<EMenu, IMenuList[]>> = {
  [EMenu.MAIN]: Menus[EMenu.MAIN],
  [EMenu.CONTENT]: Menus[EMenu.CONTENT],
};

const seniorEditorMenu: Partial<Record<EMenu, IMenuList[]>> = {
  [EMenu.MAIN]: Menus[EMenu.MAIN],
  [EMenu.CONTENT]: Menus[EMenu.CONTENT],
  [EMenu.USER]: Menus[EMenu.USER],
};

const marketingMenu: Partial<Record<EMenu, IMenuList[]>> = {
  [EMenu.MAIN]: Menus[EMenu.MAIN],
};

const writerMenu: Partial<Record<EMenu, IMenuList[]>> = {
  [EMenu.MAIN]: Menus[EMenu.MAIN],
  [EMenu.CONTENT]: Menus[EMenu.CONTENT],
};

const adminMenu: Partial<Record<EMenu, IMenuList[]>> = {
  [EMenu.MAIN]: Menus[EMenu.MAIN],
  [EMenu.OSCE]: Menus[EMenu.OSCE],
  [EMenu.TUTOR]: Menus[EMenu.TUTOR],
  [EMenu.STUDY_GROUP]: Menus[EMenu.STUDY_GROUP],
  [EMenu.UNIVERSITY]: Menus[EMenu.UNIVERSITY],
  [EMenu.CONTENT]: Menus[EMenu.CONTENT],
  [EMenu.USER]: Menus[EMenu.USER],
  // [EMenu.DATABASE]: Menus[EMenu.DATABASE],
  [EMenu.ASSET]: Menus[EMenu.ASSET],
  // [EMenu.AI]: Menus[EMenu.AI],
  [EMenu.ALGOLIA]: Menus[EMenu.ALGOLIA],
  [EMenu.SENDGRID]: Menus[EMenu.SENDGRID],
  [EMenu.SETTINGS]: Menus[EMenu.SETTINGS],
};

const superAdminMenu: Partial<Record<EMenu, IMenuList[]>> = {
  [EMenu.MAIN]: Menus[EMenu.MAIN],
  [EMenu.OSCE]: Menus[EMenu.OSCE],
  [EMenu.TUTOR]: Menus[EMenu.TUTOR],
  [EMenu.CONTENT]: Menus[EMenu.CONTENT],
  [EMenu.STUDY_GROUP]: Menus[EMenu.STUDY_GROUP],
  [EMenu.UNIVERSITY]: Menus[EMenu.UNIVERSITY],
  [EMenu.USER]: Menus[EMenu.USER],
  // [EMenu.DATABASE]: Menus[EMenu.DATABASE],
  [EMenu.ASSET]: Menus[EMenu.ASSET],
  // [EMenu.AI]: Menus[EMenu.AI],
  [EMenu.ALGOLIA]: Menus[EMenu.ALGOLIA],
  [EMenu.SENDGRID]: Menus[EMenu.SENDGRID],
  [EMenu.SETTINGS]: Menus[EMenu.SETTINGS],
  // [EMenu.SUBSCRIPTION]: Menus[EMenu.SUBSCRIPTION],
};

export const mapping: Partial<Record<IAccessLevel, Partial<Record<EMenu, IMenuList[]>>>> = {
  [IAccessLevel.EDITOR]: editorMenu,
  [IAccessLevel.SENIOR_EDITOR]: seniorEditorMenu,
  [IAccessLevel.ADMINISTRATOR]: adminMenu,
  [IAccessLevel.SUPER_ADMIN]: superAdminMenu,
  [IAccessLevel.MARKETING]: marketingMenu,
  [IAccessLevel.WRITER]: writerMenu,
};

export const getMenus = (accessLevel: IAccessLevel, isSuperAdmin: boolean, _environment: EEnvironment) => {
  const base = isSuperAdmin ? superAdminMenu : (mapping[accessLevel] ?? []);
  // const showTutor =
  //   environment === EEnvironment.DEVELOPMENT && (isSuperAdmin || accessLevel === IAccessLevel.ADMINISTRATOR);
  return base;
};
